import React, { createContext, useState, useContext, useEffect } from 'react';

// Supabase נטען בטעינה עצלה (dynamic import) כדי שלקוח ה-auth (~50KB) יישאר
// מחוץ למסלול הקריטי של דף הנחיתה. AuthContext נוגע בו רק אחרי mount (ב-useEffect).
let _supabasePromise = null;
const getSupabase = () => {
  if (!_supabasePromise) _supabasePromise = import('@/lib/supabase').then((m) => m.supabase);
  return _supabasePromise;
};

// בדף הנחיתה ובעמודי השיווק אין צורך ב-auth כדי לרנדר — דוחים את טעינת
// supabase (~50KB) ל-idle כדי שלא תתחרה על רוחב-הפס/main-thread בחלון ה-LCP.
// עמודי auth (login/register/callback) והאפליקציה המוגנת טוענים מיד.
const DEFERRABLE_AUTH_PATHS = new Set([
  "/", "/pricing", "/features", "/about", "/contact",
  "/terms", "/privacy", "/security", "/accessibility", "/articles",
]);
const canDeferAuth = () => {
  try {
    // השרת מגיש את עמודי השיווק בכתובת עם "/" בסוף (/pricing/) — מנרמלים לפני הבדיקה
    const p = window.location.pathname.replace(/(.)\/$/, "$1");
    return DEFERRABLE_AUTH_PATHS.has(p) || p.startsWith("/articles/");
  } catch { return false; }
};

// אם כבר קיים session ב-localStorage (משתמש מחובר) — לא דוחים את ה-auth, כדי
// שה-UI המחובר (תפריט החשבון/ההתנתקות) יופיע מיד ולא רק אחרי ה-idle. מבקרים
// לא-מחוברים (רוב תנועת הנחיתה, וגם Lighthouse) עדיין נהנים מהדחייה.
const hasStoredSession = () => {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith("sb-") && k.endsWith("-auth-token")) return true;
    }
  } catch { /* ignore */ }
  return false;
};

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  useEffect(() => {
    let subscription;
    let active = true;
    let idleId;

    const init = () => {
      getSupabase().then((supabase) => {
        if (!active) return;

        // Check active session on initial load
        checkUserAuth();

        // Listen for auth state changes (login, logout, token refresh)
        subscription = supabase.auth.onAuthStateChange(
          (_event, session) => {
            setUser(session?.user ?? null);
            setIsAuthenticated(!!session?.user);
            setIsLoadingAuth(false);
            setAuthChecked(true);
            if (session?.user) attributeReferral();
          }
        ).data.subscription;
      });
    };

    // נחיתה/שיווק וללא session קיים → דוחים ל-idle (עם timeout כרשת ביטחון);
    // משתמש מחובר או מסלול אפליקציה → מיד.
    if (canDeferAuth() && !hasStoredSession() && typeof requestIdleCallback === "function") {
      idleId = requestIdleCallback(init, { timeout: 2500 });
    } else {
      init();
    }

    return () => {
      active = false;
      if (idleId != null && typeof cancelIdleCallback === "function") cancelIdleCallback(idleId);
      subscription?.unsubscribe();
    };
  }, []);

  // האם זו חזרה מ-OAuth (Google) — יש code בכתובת (PKCE) או טוקן/שגיאה ב-hash.
  const isOAuthReturn = () => {
    try {
      if (new URLSearchParams(window.location.search).get("code")) return true;
      const h = new URLSearchParams(window.location.hash.replace(/^#/, ""));
      return !!(h.get("access_token") || h.get("error"));
    } catch { return false; }
  };

  // שיוך הפניה: אם נכנס דרך קישור הזמנה (?ref=) — מדווחים לשרת פעם אחת
  const attributeReferral = async () => {
    const code = localStorage.getItem("pending_ref");
    if (!code) return;
    try {
      const supabase = await getSupabase();
      await supabase.functions.invoke("referral", { body: { action: "attribute", code } });
    } catch { /* ignore */ }
    localStorage.removeItem("pending_ref");
  };

  const checkUserAuth = async () => {
    try {
      setIsLoadingAuth(true);
      const supabase = await getSupabase();
      const { data: { session }, error } = await supabase.auth.getSession();

      if (error) throw error;

      // אם אין session אבל זו חזרה מ-OAuth — החלפת ה-code עדיין רצה ברקע.
      // לא מסיימים כ"לא מחובר" (אחרת ProtectedRoute יזרוק ל-login והקוד יאבד);
      // ממתינים ל-onAuthStateChange, עם רשת ביטחון של 8 שניות.
      if (!session && isOAuthReturn()) {
        setTimeout(() => { setIsLoadingAuth(false); setAuthChecked(true); }, 8000);
        return;
      }

      setUser(session?.user ?? null);
      setIsAuthenticated(!!session?.user);
      setIsLoadingAuth(false);
      setAuthChecked(true);
    } catch (error) {
      console.error('User auth check failed:', error);
      setIsAuthenticated(false);
      setIsLoadingAuth(false);
      setAuthChecked(true);
    }
  };

  const logout = async (shouldRedirect = true) => {
    const supabase = await getSupabase();
    await supabase.auth.signOut();
    setUser(null);
    setIsAuthenticated(false);
    
    if (shouldRedirect) {
      window.location.href = '/login';
    }
  };

  const navigateToLogin = () => {
    window.location.href = '/login';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      isAuthenticated, 
      isLoadingAuth,
      authError,
      authChecked,
      logout,
      navigateToLogin,
      checkUserAuth
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
